// ============================================================
// TwDropdownContext — parent↔child coordination (TWS-122 Wave 1)
// ------------------------------------------------------------
// The dropdown family composes a parent + three slot children:
//
//   <tw-dropdown>                 (parent — owns state)
//     <button twDropdownTrigger>  (attribute directive — must
//                                  host on a real <button> so
//                                  native `popovertarget` works)
//     <tw-dropdown-menu>          (slot — hosts the popover panel)
//       <tw-dropdown-item>        (slot — menu row)
//
// The trigger needs the parent's generated `popoverId` (for
// `popovertarget`) and the `open` state (for `aria-expanded`).
// The menu needs the same id, the anchor-name, and a callback to
// sync its native `toggle` event back to the parent's `open`
// model. Items need to notify the menu on selection.
//
// Rather than pass these through inputs/outputs (which would
// force consumers to thread refs manually), the parent provides
// an injection token that children consume. Slots are always
// rendered inside a parent, so the DI tree always resolves.
// ============================================================

import { InjectionToken, Signal } from '@angular/core';

import { TwPopoverPlacement } from '../shared/types';

/**
 * Placement hints the parent passes through to the menu.
 * `top` / `bottom` flip on the block axis; `start` / `end` flip on
 * the inline axis (RTL-safe).
 *
 * Wave 2 (TWS-122) lifted the canonical type to
 * `TwPopoverPlacement` (shared across dropdown, tooltip, and
 * popover). `TwDropdownPlacement` is retained as a re-aliased
 * name for backwards compatibility with any consumer that already
 * imports the dropdown-scoped name from the public barrel.
 */
export type TwDropdownPlacement = TwPopoverPlacement;

/**
 * Shape the parent publishes through `TW_DROPDOWN_CONTEXT`.
 * Intentionally narrow — only what the slot components actually
 * read. Expanding this surface is a breaking change for out-of-
 * tree consumers that might build custom dropdown slot variants.
 */
export interface TwDropdownContext {
  /** Generated DOM id used for `popovertarget` ↔ `id` wiring. */
  readonly popoverId: string;
  /** Generated CSS custom-property name for anchor wiring. */
  readonly anchorName: string;
  /** Current open state (reactive, read by trigger for aria-expanded). */
  readonly open: Signal<boolean>;
  /** Placement hint — the menu uses it to select a positioning mixin. */
  readonly placement: Signal<TwDropdownPlacement>;
  /** Called by the menu when the native `toggle` event flips state. */
  setOpen(value: boolean): void;
  /** Called by the trigger on `ArrowDown` — open + focus first item. */
  requestOpenAndFocusFirst(): void;
  /** Called by items on selection — close + return focus to the trigger. */
  requestClose(): void;
  /** Called by the menu on Escape — close + return focus to the trigger. */
  requestCloseAndReturnFocus(): void;
  /** Registers the trigger's host element so focus can return on close. */
  registerTriggerElement(el: HTMLElement): void;
  /** The menu calls this to register itself so items can reach it. */
  registerMenuElement(el: HTMLElement): void;
}

/**
 * DI token the parent component provides to its subtree. Children
 * inject it via `inject(TW_DROPDOWN_CONTEXT)` — the type is
 * non-optional because a slot rendered outside a parent is a
 * consumer error, and Angular's missing-provider error surfaces
 * exactly that miswiring.
 */
export const TW_DROPDOWN_CONTEXT = new InjectionToken<TwDropdownContext>(
  'TW_DROPDOWN_CONTEXT',
);
